// Typing in the editor as in Notion (decision 034, um/2026-10-30/03 п.2): Enter and Backspace between blocks, arrows across them,
// markdown shortcuts at the start of a line, «/» for the kind of a block, marks by hotkeys and pasting text as blocks.

import type React from 'react';
import { newBlock, parseBlocks, type Block, type BlockKind } from '@confdeck/core-shared';
import { caretAtEnd, caretAtStart, caretOffset, caretPoint, deleteText, onEdgeLine, splitAtCaret } from './caret.ts';
import { domToInline } from './dom-inline.ts';
import { findKinds, LIST_KINDS, shortcutOf, TEXT_KINDS } from './kinds.ts';
import type { EditorCtl } from './ctl.ts';

const mod = (e: React.KeyboardEvent) => e.metaKey || e.ctrlKey;

/** The block's content as stored: code is plain text, the rest is inline markdown. */
export const readBlock = (b: Block, el: HTMLElement) => (b.kind === 'code' ? (el.textContent ?? '').replace(/\n$/, '') : domToInline(el));

/** A kind chosen in «/» or «+»: an empty block becomes it, a block with text gets a new one below. */
export function applyKind(ctl: EditorCtl, id: string, kind: BlockKind): void {
  const s = ctl.slash;
  const el = ctl.el(id);
  const b = ctl.get(id);
  ctl.slash = null;
  if (!b) return;
  if (s && s.id === id && el) { deleteText(el, s.from, s.query.length + 1); ctl.setText(id, readBlock(b, el)); }
  const empty = !ctl.get(id)!.text.trim();
  if (kind === 'file' || kind === 'record') { ctl.render(); ctl.ask(kind, id); return; }
  if (kind === 'divider') {
    const line = newBlock('divider');
    const next = newBlock('p');
    if (empty) ctl.commit(ctl.blocks.flatMap((x) => (x.id === id ? [line, next] : [x])));
    else ctl.commit(ctl.blocks.flatMap((x) => (x.id === id ? [x, line, next] : [x])));
    ctl.focus(next.id, 0);
    return;
  }
  if (empty) ctl.turnInto(id, kind);
  else ctl.insertAfter(id, [newBlock(kind, '', kind === 'todo' ? { checked: false } : {})], 0);
  if (empty && kind === 'p') ctl.render();
}

export function handleKey(ctl: EditorCtl, e: React.KeyboardEvent<HTMLElement>, b: Block): void {
  const el = e.currentTarget;
  const i = ctl.index(b.id);
  const prev = ctl.blocks[i - 1];
  const next = ctl.blocks[i + 1];
  const s = ctl.slash?.id === b.id ? ctl.slash : null;
  if (s) {
    const list = findKinds(s.query, ctl.kinds);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); s.active = (s.active + (e.key === 'ArrowDown' ? 1 : list.length - 1)) % Math.max(1, list.length); ctl.render(); return; }
    if ((e.key === 'Enter' || e.key === 'Tab') && list.length) { e.preventDefault(); applyKind(ctl, b.id, list[s.active]!.kind); return; }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); ctl.slash = null; ctl.render(); return; }
  }
  // A divider, a file and a record are one piece: Enter adds a line after, Backspace removes them.
  if (!TEXT_KINDS.has(b.kind)) {
    if (e.key === 'Enter') { e.preventDefault(); ctl.insertAfter(b.id, [newBlock('p')], 0); }
    else if (e.key === 'Backspace' || e.key === 'Delete') { e.preventDefault(); ctl.remove(b.id); }
    else if ((e.key === 'ArrowUp' || e.key === 'ArrowLeft') && prev) { e.preventDefault(); ctl.focus(prev.id, 'end'); }
    else if ((e.key === 'ArrowDown' || e.key === 'ArrowRight') && next) { e.preventDefault(); ctl.focus(next.id, 0); }
    return;
  }
  if (mod(e) && e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) { e.preventDefault(); ctl.moveBy(b.id, e.key === 'ArrowUp' ? -1 : 1); return; }
  if (mod(e) && b.kind !== 'code' && !e.altKey) {
    const k = e.key.toLowerCase();
    const cmd = k === 'b' || k === 'и' ? 'bold' : k === 'i' || k === 'ш' ? 'italic' : e.shiftKey && (k === 's' || k === 'ы' || k === 'x' || k === 'ч') ? 'strikeThrough' : null;
    if (cmd) { e.preventDefault(); document.execCommand(cmd); ctl.setText(b.id, readBlock(b, el)); return; }
    if (k === 'e' || k === 'у') { e.preventDefault(); wrapCode(el); ctl.setText(b.id, readBlock(b, el)); return; }
  }
  if (e.key === 'Escape') { e.stopPropagation(); el.blur(); return; }
  if (e.key === 'Tab' && b.kind === 'code') { e.preventDefault(); document.execCommand('insertText', false, '  '); return; }
  if (e.key === 'Enter') {
    if (b.kind === 'code' && !mod(e)) { e.preventDefault(); document.execCommand('insertText', false, '\n'); return; }
    if (e.shiftKey && (b.kind === 'p' || b.kind === 'quote' || b.kind === 'callout')) return;
    e.preventDefault();
    if (LIST_KINDS.has(b.kind) && !(el.textContent ?? '').trim()) { ctl.turnInto(b.id, 'p'); return; }
    if (b.kind === 'code') { ctl.insertAfter(b.id, [newBlock('p')], 0); return; }
    const { before, after } = splitAtCaret(el);
    ctl.split(b.id, before, after);
    return;
  }
  if (e.key === 'Backspace' && caretAtStart(el)) {
    if (b.kind !== 'p') { e.preventDefault(); if (b.kind !== 'code' || !(el.textContent ?? '')) ctl.turnInto(b.id, 'p'); return; }
    if (!prev) { if (!b.text && next) { e.preventDefault(); ctl.remove(b.id); } return; }
    e.preventDefault();
    ctl.mergeUp(b.id, readBlock(b, el), ctl.el(prev.id)?.textContent?.length ?? 0);
    return;
  }
  if (e.key === 'Delete' && caretAtEnd(el) && next) { e.preventDefault(); ctl.mergeDown(b.id, readBlock(b, el), el.textContent?.length ?? 0); return; }
  if (e.key === 'ArrowUp' && prev && onEdgeLine(el, 'first')) { e.preventDefault(); ctl.focus(prev.id, 'end'); return; }
  if (e.key === 'ArrowDown' && next && onEdgeLine(el, 'last')) { e.preventDefault(); ctl.focus(next.id, 0); return; }
  if (e.key === 'ArrowDown' && !next && b.kind === 'code' && onEdgeLine(el, 'last')) { e.preventDefault(); ctl.insertAfter(b.id, [newBlock('p')], 0); return; }
  if (e.key === 'ArrowLeft' && prev && caretAtStart(el)) { e.preventDefault(); ctl.focus(prev.id, 'end'); return; }
  if (e.key === 'ArrowRight' && next && caretAtEnd(el)) { e.preventDefault(); ctl.focus(next.id, 0); }
}

/** Wraps the selection into `code`, or unwraps the code the caret is in. */
function wrapCode(el: HTMLElement): void {
  const sel = getSelection();
  if (!sel || !sel.rangeCount) return;
  const inCode = (sel.anchorNode?.parentElement)?.closest('code');
  if (inCode && el.contains(inCode)) { inCode.replaceWith(document.createTextNode(inCode.textContent ?? '')); return; }
  if (sel.isCollapsed) return;
  const r = sel.getRangeAt(0);
  const code = document.createElement('code');
  code.textContent = r.toString();
  r.deleteContents();
  r.insertNode(code);
  sel.collapse(code, 1);
}

/** After each typed character: markdown shortcuts of a paragraph, the «/» menu, and the block's text. */
export function handleInput(ctl: EditorCtl, el: HTMLElement, b: Block): void {
  // The person types where their caret is: a caret move still waiting for a render is stale and would put the next key elsewhere.
  if (ctl.focusReq?.id === b.id) ctl.focusReq = null;
  if (!el.textContent && !el.querySelector('img')) el.innerHTML = '';
  const raw = (el.textContent ?? '').replace(/ /g, ' ');
  if (b.kind === 'p') {
    const sc = shortcutOf(raw);
    if (sc && ctl.allows(sc.kind) && caretOffset(el) === sc.length) {
      deleteText(el, 0, sc.length);
      if (sc.kind === 'divider') { ctl.setText(b.id, ''); applyKind(ctl, b.id, 'divider'); return; }
      ctl.patch(b.id, { kind: sc.kind, text: readBlock(b, el), ...(sc.kind === 'todo' ? { checked: !!sc.checked } : {}) });
      ctl.focus(b.id, 0);
      return;
    }
  }
  // A text of paragraphs only has nothing to choose in «/».
  if (b.kind !== 'code' && (!ctl.kinds || ctl.kinds.size > 1)) {
    const at = caretOffset(el);
    const m = at === null ? null : /(^|\s)\/([^\s/]{0,24})$/.exec(raw.slice(0, at));
    if (m && at !== null) {
      const query = m[2]!;
      const p = caretPoint(el);
      const same = ctl.slash?.id === b.id;
      ctl.slash = { id: b.id, query, from: at - query.length - 1, active: same && ctl.slash!.query === query ? ctl.slash!.active : 0, x: same ? ctl.slash!.x : p.x, y: p.y };
    } else if (ctl.slash?.id === b.id) ctl.slash = null;
  }
  const text = readBlock(b, el);
  if (text !== b.text) ctl.setText(b.id, text); else ctl.render();
}

/** Paste: files go up as file blocks, a link over selected text makes it a link, lines of text become blocks. */
export function handlePaste(ctl: EditorCtl, e: React.ClipboardEvent<HTMLElement>, b: Block, onFiles: (id: string, files: File[]) => void): void {
  const el = e.currentTarget;
  const files = [...e.clipboardData.files];
  if (files.length) { e.preventDefault(); onFiles(b.id, files); return; }
  const text = e.clipboardData.getData('text/plain');
  e.preventDefault();
  if (!text) return;
  if (b.kind === 'code') { document.execCommand('insertText', false, text); return; }
  const sel = getSelection();
  if (/^https?:\/\/\S+$/.test(text.trim()) && sel && !sel.isCollapsed) { document.execCommand('createLink', false, text.trim()); ctl.setText(b.id, readBlock(b, el)); return; }
  if (!/\n/.test(text.trim())) { document.execCommand('insertText', false, text.replace(/\s*\n\s*/g, ' ')); return; }
  const add = ctl.fit(parseBlocks(text));
  if (!add.length) return;
  const { before, after } = splitAtCaret(el);
  ctl.pasteBlocks(b.id, before, after, add);
}
